import React from 'react';
import { Link } from 'react-router-dom';
import { 
  GlobeAltIcon,
  SparklesIcon,
  CheckBadgeIcon,
  TagIcon,
  TrophyIcon,
  HeartIcon,
  BoltIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';

const About: React.FC = () => {
  const features = [
    {
      icon: BoltIcon,
      title: "Performance Shorts",
      description: "Designed for comfort and mobility."
    },
    {
      icon: SparklesIcon,
      title: "Sport T-Shirts",
      description: "Lightweight, breathable, and perfect for any activity."
    },
    {
      icon: TrophyIcon,
      title: "Polo Shirts",
      description: "Stylish for both casual outings and the golf course."
    },
    {
      icon: HeartIcon,
      title: "Tracksuits",
      description: "Ideal for warm-ups or casual athleisure wear."
    },
    {
      icon: ScaleIcon,
      title: "Tennis Skirts & Activewear",
      description: "For women who demand both style and functionality."
    }
  ];

  const whyChooseUs = [
    {
      icon: CheckBadgeIcon,
      title: "Premium First-Copy Branded Apparel",
      description: "High-quality replicas of top brands at affordable prices"
    },
    {
      icon: TrophyIcon,
      title: "Sports-Focused Collections",
      description: "Specialized in activewear and sports-inspired clothing"
    },
    {
      icon: GlobeAltIcon,
      title: "Direct Factory Imports",
      description: "Sourced directly from top factories in Vietnam and Thailand"
    },
    {
      icon: TagIcon,
      title: "Fair and Competitive Pricing",
      description: "Quality apparel at prices that won't break the bank"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-[41px]">
      {/* Breadcrumb Section */}
      <div className="border-b border-gray-200 py-4" style={{ backgroundColor: '#f7f1f0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Page Title */}
            <h1 className="text-2xl font-medium text-gray-600 tracking-wide mb-2">ABOUT US</h1>
            {/* Breadcrumb Navigation */}
            <nav className="flex items-center justify-center space-x-2 text-xs" aria-label="Breadcrumb">
              <Link 
                to="/" 
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">About Us</span>
            </nav>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative  text-black py-10">
        <div className="absolute inset-0"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            Welcome to Signature Styles
          </h1>
          <p className="text-xl max-w-3xl mx-auto">
            Premium sportswear for the active lifestyle
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-16">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Welcome to Signature Styles, where fashion, sports, and affordability come together. Founded in June 2024, we specialize in providing high-quality first-copy branded clothing directly imported from top factories in Vietnam, Thailand, and other renowned hubs.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Our mission is simple: to offer stylish, premium apparel that caters to both fashion enthusiasts and sports lovers—all at fair prices. From Nike and Adidas to Under Armour and Puma, our collection includes a wide range of shorts, T-shirts, polo shirts, tracksuits, and more.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-12">
              At Signature Styles, we focus on delivering clothing designed for active lifestyles. Whether you're on the golf course, at the gym, or enjoying a casual day out, our sport-inspired collections ensure you look and feel your best. With advanced designs and comfortable fits, our products are tailored for movement and performance.
            </p>
          </div>

          {/* Our Focus on Sports Apparel */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Our Focus on Sports Apparel
            </h2>
            <p className="text-lg text-gray-700 text-center mb-12">
              Explore our premium range of sport-related clothing, including:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center mb-4">
                    <feature.icon className="h-8 w-8 text-gray-600 mr-3" />
                    <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                  </div>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-lg text-gray-700 italic">
                We believe that sportswear should be both high-performing and stylish. That's why we ensure every piece is crafted to match your active lifestyle.
              </p>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Why Choose Us?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {whyChooseUs.map((item, index) => (
                <div key={index} className="flex items-start space-x-4 bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex-shrink-0">
                    <item.icon className="h-8 w-8 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-gray-800 rounded-lg p-8 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Join Our Journey
            </h2>
            <p className="text-lg mb-6">
              Join us on our journey to redefine affordable activewear and style. At Signature Styles, quality isn't just a promise – it's a guarantee.
            </p>
            <p className="text-xl font-semibold mb-8">
              Explore our collection today and feel the difference!
            </p>
            <Link 
              to="/product-signature" 
              className="inline-block bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300 transform hover:scale-105"
            >
              Shop Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
